import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import ApiServices from "../../config/ApiServices";
import { showLoader, hideLoader } from "../../redux/actions/LoaderActions";
import Loader from "../../utils/Loader";
import { toast } from "react-toastify";
import AttachmentIcon from '@mui/icons-material/Attachment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import { showModal } from "../../redux/actions/ModalActions";

export default function ViewSubmissions() {
    const dispatch = useDispatch();
    const loading = useSelector((state) => state.loader.loading);
    const { taskId } = useParams();
    const [submissions, setSubmissions] = useState([]);
    const [marks, setMarks] = useState([]);
    const [newMarks, setNewMarks] = useState({});
    const [errors, setErrors] = useState({});
    const [maxMarks, setMaxMarks] = useState(0);

    useEffect(() => {
        fetchSubmissions();
        fetchMarks();
        fetchTaskDetails();
    }, [taskId]);

    const fetchSubmissions = () => {
        dispatch(showLoader());
        ApiServices.apiRequest("getUploadedTask", { taskId })
            .then((res) => {
                if (res.data.success) setSubmissions(res.data.data);
                else toast.error(res.data.message);
            })
            .catch((err) => toast.error(err?.message || "Something went wrong"))
            .finally(() => dispatch(hideLoader()));
    };

    const fetchMarks = () => {
        dispatch(showLoader());
        ApiServices.apiRequest("getMarks", { testId: taskId })
            .then((res) => {
                if (res.data.success) setMarks(res.data.data);
                else toast.error(res.data.message);
            })
            .catch((err) => toast.error(err?.message || "Something went wrong"))
            .finally(() => dispatch(hideLoader()));
    };

    const fetchTaskDetails = () => {
        dispatch(showLoader());
        ApiServices.apiRequest("getSingleTask", { _id: taskId })
            .then((res) => {
                if (res.data.success) setMaxMarks(res.data.data.maximumMarks);
            })
            .catch((err) => toast.error(err?.message || "Something went wrong"))
            .finally(() => dispatch(hideLoader()));
    };

    const getStudentMarks = (studentId) => {
        const mark = marks.find(m => m.studentId?._id === studentId);
        return mark ? mark.score : null;
    };

    const getStudentMarksId = (studentId) => {
        const mark = marks.find(m => m.studentId?._id === studentId);
        return mark ? mark._id : null;
    };

    const handleMarksChange = (studentId, value) => {
        if (/^\d*$/.test(value)) {
            setNewMarks({ ...newMarks, [studentId]: value });
            setErrors({ ...errors, [studentId]: null });
        }
    };

    const handleBlur = (studentId) => {
        const value = newMarks[studentId];
        if (value === "" || value < 0 || value > maxMarks) {
            setErrors({ ...errors, [studentId]: `Please enter a number between 0 and ${maxMarks}` });
        } else {
            setErrors({ ...errors, [studentId]: null });
        }
    };

    const handleAssignMarks = (studentId, score) => {
        if (score === "" || score === undefined) {
            toast.error("Please enter marks first");
            return;
        }
        const data = { studentId, score, testId: taskId };
        ApiServices.apiRequest("assignMarks", data)
            .then((res) => {
                if (res.data.success) {
                    toast.success("Marks assigned successfully");
                    fetchMarks();
                } else {
                    toast.error(res.data.message);
                }
            })
            .catch((err) => toast.error(err?.message || "Something went wrong"));
    };

    const handleUpdateMarks = (studentId, score) => {
        const marksId = getStudentMarksId(studentId);
        if (!marksId || score === undefined || score === "") {
            toast.error("Invalid marks data");
            return;
        }
        const data = { _id: marksId, score };
        dispatch(showModal("StatusModal", data, "updateMarks", "updateMarks"));
    };

    const getHistory = (studentId) => {
        const history = marks.find(m => m.studentId?._id === studentId);
        dispatch(showModal("MarksHistory", history));
    };

    const getAttachmentName = (filename) => {
        let parts = filename?.split('-');
        return parts?.[1] || filename;
    };

    return (
        <div className="container">
            <div className="page-inner">
                {loading ? <Loader /> : (
                    <>
                        <h3 className="fw-bold mt-3 mb-3">Task Submissions</h3>
                        {submissions?.length > 0 ? submissions.map((sub, idx) => {
                            const hasMarks = getStudentMarks(sub.studentId?._id) !== null;
                            const currentValue = newMarks[sub.studentId?._id] ?? (hasMarks ? getStudentMarks(sub.studentId?._id) : "");

                            return (
                                <div className="card my-2" key={idx}>
                                    <div className="card-body d-flex justify-content-between">
                                        <div>
                                            <h5>{sub.studentId?.name}</h5>
                                            <p className="mb-1">{sub.studentId?.email}</p>
                                            <div>
                                                {sub.attachments?.map((file, i) => (
                                                    <div key={i} className="d-flex align-items-center mb-1">
                                                        <Link to={file} target="_blank" rel="noopener noreferrer">
                                                            <AttachmentIcon sx={{ ml: 1, color: 'grey' }} />
                                                            <span className="ms-2">{getAttachmentName(file)}</span>
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <input
                                                type="text"
                                                className="form-control d-inline-block ms-2 rounded-0 text-center"
                                                style={{ width: "40px", height: "20px", padding: "0px", border: "none", borderBottom: "1px solid #ccc", background: "transparent" }}
                                                value={currentValue}
                                                onChange={(e) => handleMarksChange(sub.studentId?._id, e.target.value)}
                                                onBlur={() => handleBlur(sub.studentId?._id)}
                                            /> / {maxMarks}
                                            {errors[sub.studentId?._id] && <div className="text-danger" style={{ fontSize: "12px" }}>{errors[sub.studentId?._id]}</div>}
                                            <div className="dropdown mt-1">
                                                <IconButton type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                    <MoreVertIcon />
                                                </IconButton>
                                                <ul className="dropdown-menu">
                                                    {!hasMarks && (
                                                        <li>
                                                            <a className="dropdown-item" href="#" onClick={() => handleAssignMarks(sub.studentId?._id, currentValue)}>Return</a>
                                                        </li>
                                                    )}
                                                    {hasMarks && (
                                                        <>
                                                            <li>
                                                                <a className="dropdown-item" href="#" onClick={() => handleUpdateMarks(sub.studentId?._id, currentValue)}>Update</a>
                                                            </li>
                                                            <li>
                                                                <a className="dropdown-item" href="#" onClick={() => getHistory(sub.studentId?._id)}>History</a>
                                                            </li>
                                                        </>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }) : <h5>No submissions found</h5>}
                    </>
                )}
            </div>
        </div>
    );
}
